import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../../button/Button';
import Input from '../../../form/Input';
import Select from '../../../form/Select';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { updateUrlWithParams } from '../../../../util/util';
import { useFranchiseStore } from '../../../../store/useFranchise';
import { useLoginStore } from '../../../../store/useLogin';
import { usePurchaseOrderStore } from '../../../../store/usePurchaseOrderStore';

/**
 * @author assistant
 * @date 2025.01.05
 * @description 입금대기 목록 필터
 */
interface IPurchaseOrderFilter {
  getFilteredData: () => void;
}

const PurchaseOrderFilter = ({ getFilteredData }: IPurchaseOrderFilter) => {
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);
  const { role } = useLoginStore();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isAdmin = role === 'ROLE_ADMIN';
  
  const getDefaultDateRange = () => {
    const today = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(today.getDate() - 14);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(today)
    };
  };

  const defaultDates = getDefaultDateRange();
  
  const [purchaseType, setPurchaseType] = useState<'WAIT' | 'SUCCESS' | 'REJECT' | ''>('');
  const [franchiseId, setFranchiseId] = useState('');
  const [startDt, setStartDt] = useState(defaultDates.start);
  const [endDt, setEndDt] = useState(defaultDates.end);
  const [size, setSize] = useState(20);

  const { franchises, getFranchiseLoading, getFranchise } = useFranchiseStore();

  useEffect(() => {
    if(role === 'ROLE_ADMIN') {
      getFranchise({ size: 1000 }); // 전체 가맹점 목록 조회
    }
  }, [getFranchise]);

  // URL 파라미터가 변경될 때마다 필터 상태 동기화
  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    setPurchaseType((parsedQuery.purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT') || '');
    setFranchiseId((parsedQuery.franchiseId as string) || '');
    setStartDt((parsedQuery.StartDt as string) || defaultDates.start);
    setEndDt((parsedQuery.EndDt as string) || defaultDates.end);
    setSize(parsedQuery.size ? Number(parsedQuery.size) : 20);
  }, [location.search, defaultDates.start, defaultDates.end]);

  const purchaseTypeOptions = [
    { option: '전체', value: '' },
    { option: '대기', value: 'WAIT' },
    { option: '승인', value: 'SUCCESS' },
    { option: '거절', value: 'REJECT' },
  ];

  const franchiseOptions = [
    { option: '전체 가맹점', value: '' },
    ...franchises.map(franchise => ({
      option: franchise.companyName,
      value: franchise.id,
    })),
  ];

  const sizeOptions = [
    { option: '20개씩', value: '20' },
    { option: '50개씩', value: '50' },
    { option: '100개씩', value: '100' },
  ];

  const handleSearch = useCallback(() => {
    console.log('Filter values:', { purchaseType, franchiseId, startDt, endDt, size }); // 디버깅용
    
    const queryParams = {
      purchaseType,
      franchiseId,
      StartDt: startDt,
      EndDt: endDt,
      size,
      page: 0,
    };

    updateUrlWithParams(queryParams, navigate);
    
    // getFilteredData 대신 직접 API 호출하여 현재 state 값 사용
    setTimeout(() => {
      const { getPurchaseOrders } = usePurchaseOrderStore.getState();
      getPurchaseOrders({
        purchaseType: purchaseType || undefined,
        franchiseId: franchiseId || undefined,
        startDt,
        endDt,
        size,
        page: 0,
      });
    }, 100);
  }, [purchaseType, franchiseId, startDt, endDt, size, navigate]);

  return (
    <div className="!w-full">
      <div className="!flex !justify-between !items-center">
        <button
          onClick={() => setIsFilterOpen(prev => !prev)}
          className="md:!hidden !px-3 !py-1.5 !border !rounded !text-sm"
        >
          {isFilterOpen ? '닫기' : '검색필터 열기'}
        </button>
      </div>
      <form
        onSubmit={(e) => e.preventDefault()}
        className={`
          ${isFilterOpen ? '!flex' : '!hidden'}
          md:!flex !mt-4 !flex-col !gap-4 md:!flex-row md:!items-center !w-full
        `}
      >
        <div className="!flex !items-center !gap-3 !flex-wrap">
          <Select
            value={purchaseType}
            onChange={(e) => setPurchaseType(e.target.value as 'WAIT' | 'SUCCESS' | 'REJECT' | '')}
            options={purchaseTypeOptions}
            style={{ width: '120px' }}
          />
          
          {isAdmin && (
            <Select
              value={franchiseId}
              onChange={(e) => setFranchiseId(e.target.value)}
              options={franchiseOptions}
              style={{ width: '200px', opacity: getFranchiseLoading ? 0.5 : 1 }}
            />
          )}
          
          <Input
            type="date"
            value={startDt}
            onChange={(e) => setStartDt(e.target.value)}
            width={150}
          />
          
          <span className="!text-gray-500">~</span>
          
          <Input
            type="date"
            value={endDt}
            onChange={(e) => setEndDt(e.target.value)}
            width={150}
          />
          
          <Select
            value={String(size)}
            onChange={(e) => setSize(Number(e.target.value))}
            options={sizeOptions}
            style={{ width: '100px' }}
          />
          <button
              type="button"
              onClick={handleSearch}
              className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
          >
            검색
          </button>
        </div>
      </form>
    </div>
  );
};

export default PurchaseOrderFilter;